/**
 * Adaptateurs des domaines trésorerie, alertes et dépenses.
 *
 * Regroupés parce qu'aucun ne dépasse quelques requêtes ; ils se sépareront
 * le jour où l'un d'eux grossira.
 */
import type {
  Alert,
  AlertRepository,
  CashflowEntry,
  ExecutionContext,
  Expense,
  ExpenseCategory,
  ExpenseFilter,
  ExpenseRepository,
  SaveExpenseCommand,
  TenantRepository,
  TenantSettings,
  TreasuryRepository,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown) => Number(v ?? 0);

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

// ─── Trésorerie ────────────────────────────────────────────────────────────

export function createSupabaseTreasuryRepository(
  clientFor: DbClientFactory,
): TreasuryRepository {
  return {
    async paymentsSince(ctx: ExecutionContext, from: string): Promise<CashflowEntry[]> {
      // Une délégation de paiement solde la facture, mais le client a payé le
      // sous-traitant : rien n'est entré. `neq` seul écarterait aussi les
      // règlements sans moyen.
      const { data, error } = await clientFor(ctx)
        .from("payments")
        .select("amount, payment_date")
        .gte("payment_date", from)
        .or("payment_method.is.null,payment_method.neq.delegation");
      if (error) fail(error);
      return (data ?? []).map((r: Row) => ({
        date: r.payment_date,
        amount: num(r.amount),
      }));
    },

    async expensesSince(ctx: ExecutionContext, from: string): Promise<CashflowEntry[]> {
      const db = clientFor(ctx);
      const [expenses, sousTraitance] = await Promise.all([
        db.from("expenses").select("amount_ttc, expense_date").gte("expense_date", from),
        // Ce qu'on a réglé aux sous-traitants : leurs factures ne sont pas des
        // dépenses, et la retenue ne sort qu'au jour où on la rend. Ce que le
        // client leur a payé, par délégation, ne sort pas d'ici.
        db.from("subcontract_payments").select("amount, paid_on").gte("paid_on", from).eq("paid_by", "company"),
      ]);
      if (expenses.error) fail(expenses.error);
      if (sousTraitance.error) fail(sousTraitance.error);
      return [
        ...(expenses.data ?? []).map((r: Row) => ({
          date: r.expense_date,
          amount: num(r.amount_ttc),
        })),
        ...(sousTraitance.data ?? []).map((r: Row) => ({
          date: r.paid_on,
          amount: num(r.amount),
        })),
      ];
    },

    async expectedIncomeSince(
      ctx: ExecutionContext,
      from: string,
    ): Promise<CashflowEntry[]> {
      const { data, error } = await clientFor(ctx)
        .from("invoices")
        .select("total_ttc, amount_paid, due_date")
        .in("status", ["sent", "partially_paid", "overdue"])
        .gte("due_date", from);
      if (error) fail(error);

      // Seul le solde restant est attendu : projeter le total ferait compter
      // deux fois un acompte déjà encaissé.
      return (data ?? [])
        .map((r: Row) => ({
          date: r.due_date,
          amount: Math.max(0, num(r.total_ttc) - num(r.amount_paid)),
        }))
        .filter((e: CashflowEntry) => e.amount > 0);
    },
  };
}

// ─── Alertes ───────────────────────────────────────────────────────────────

export function createSupabaseAlertRepository(
  clientFor: DbClientFactory,
): AlertRepository {
  const toAlert = (row: Row): Alert => ({
    id: row.id,
    type: row.type,
    severity: row.severity,
    title: row.title,
    message: row.message,
    relatedEntityType: row.related_entity_type ?? null,
    relatedEntityId: row.related_entity_id ?? null,
    actionLabel: row.action_label ?? null,
    actionUrl: row.action_url ?? null,
    isRead: Boolean(row.is_read),
    createdAt: row.created_at,
  });

  return {
    async listActive(ctx: ExecutionContext): Promise<Alert[]> {
      const { data, error } = await clientFor(ctx)
        .from("ai_alerts")
        .select("*")
        .eq("is_dismissed", false)
        .order("created_at", { ascending: false });
      if (error) fail(error);
      return (data ?? []).map(toAlert);
    },

    async dismiss(ctx: ExecutionContext, alertIds: string[]): Promise<number> {
      const { error } = await clientFor(ctx)
        .from("ai_alerts")
        .update({ is_dismissed: true })
        .in("id", alertIds);
      if (error) fail(error);
      return alertIds.length;
    },

    async markRead(ctx: ExecutionContext, alertIds: string[]): Promise<number> {
      const { error } = await clientFor(ctx)
        .from("ai_alerts")
        .update({ is_read: true })
        .in("id", alertIds);
      if (error) fail(error);
      return alertIds.length;
    },
  };
}

// ─── Dépenses ──────────────────────────────────────────────────────────────

/** PostgREST refuse les charges trop volumineuses : on découpe l'import. */
const CHUNK = 40;

export function createSupabaseExpenseRepository(
  clientFor: DbClientFactory,
): ExpenseRepository {
  const toExpense = (row: Row): Expense => {
    const category = Array.isArray(row.category) ? row.category[0] : row.category;
    const project = Array.isArray(row.project) ? row.project[0] : row.project;
    return {
      id: row.id,
      description: row.description,
      amountHt: num(row.amount_ht),
      tvaRate: num(row.tva_rate),
      amountTtc: num(row.amount_ttc),
      expenseDate: row.expense_date,
      paymentMethod: row.payment_method ?? null,
      receiptUrl: row.receipt_url ?? null,
      supplierId: row.supplier_id ?? null,
      category: category
        ? { id: category.id, name: category.name, color: category.color ?? null }
        : null,
      project: project ? { id: project.id, name: project.name } : null,
    };
  };

  const toColumns = (c: SaveExpenseCommand): Row => ({
    description: c.description,
    amount_ht: c.amountHt,
    tva_rate: c.tvaRate,
    amount_ttc: c.amountTtc,
    expense_date: c.expenseDate,
    payment_method: c.paymentMethod,
    category_id: c.categoryId,
    project_id: c.projectId,
    supplier_id: c.supplierId,
    receipt_url: c.receiptUrl,
  });

  return {
    async list(ctx: ExecutionContext, filter?: ExpenseFilter): Promise<Expense[]> {
      let query = clientFor(ctx)
        .from("expenses")
        .select(
          "*, category:expense_categories(id, name, color), project:projects(id, name)",
        )
        .order("expense_date", { ascending: false });

      if (filter?.from) query = query.gte("expense_date", filter.from);
      if (filter?.projectId) query = query.eq("project_id", filter.projectId);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toExpense);
    },

    async save(ctx: ExecutionContext, command: SaveExpenseCommand): Promise<Expense> {
      const db = clientFor(ctx);
      const columns = toColumns(command);
      const selection =
        "*, category:expense_categories(id, name, color), project:projects(id, name)";

      const { data, error } = command.expenseId
        ? await db
            .from("expenses")
            .update(columns)
            .eq("id", command.expenseId)
            .select(selection)
            .maybeSingle()
        : await db.from("expenses").insert(columns).select(selection).single();

      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Cette dépense n'existe pas.", {
          expenseId: command.expenseId,
        });
      }
      return toExpense(data);
    },

    async saveMany(
      ctx: ExecutionContext,
      commands: SaveExpenseCommand[],
    ): Promise<number> {
      const db = clientFor(ctx);
      let inserted = 0;

      for (let i = 0; i < commands.length; i += CHUNK) {
        const slice = commands.slice(i, i + CHUNK).map(toColumns);
        const { error } = await db.from("expenses").insert(slice);
        if (error) {
          throw new DomainError(
            "conflict",
            `Import interrompu après ${inserted} dépense(s) : ${error.message}. ` +
              `Les lignes déjà importées ont été conservées.`,
            { imported: inserted, pgCode: error.code },
          );
        }
        inserted += slice.length;
      }
      return inserted;
    },

    async listCategories(ctx: ExecutionContext): Promise<ExpenseCategory[]> {
      const { data, error } = await clientFor(ctx)
        .from("expense_categories")
        .select("id, name, color, is_default")
        .order("name");
      if (error) fail(error);
      return (data ?? []).map((r: Row) => ({
        id: r.id,
        name: r.name,
        color: r.color ?? null,
        isDefault: Boolean(r.is_default),
      }));
    },

    async createCategory(
      ctx: ExecutionContext,
      name: string,
      color: string | null,
    ): Promise<ExpenseCategory> {
      const { data, error } = await clientFor(ctx)
        .from("expense_categories")
        .insert({ name, color })
        .select("id, name, color, is_default")
        .single();
      if (error) fail(error);
      return {
        id: data.id,
        name: data.name,
        color: data.color ?? null,
        isDefault: Boolean(data.is_default),
      };
    },

    async ensureDefaultCategories(ctx: ExecutionContext): Promise<void> {
      const { error } = await clientFor(ctx).rpc(
        "ensure_default_expense_categories_for_my_tenant",
      );
      if (error) fail(error);
    },
  };
}

// ─── Paramètres entreprise ─────────────────────────────────────────────────

export function createSupabaseTenantRepository(
  clientFor: DbClientFactory,
): TenantRepository {
  return {
    async update(ctx: ExecutionContext, patch): Promise<TenantSettings> {
      const { data, error } = await clientFor(ctx)
        .from("tenants")
        .update({
          name: patch.name,
          legal_name: patch.legalName,
          legal_form: patch.legalForm,
          rcs: patch.rcs,
          siret: patch.siret,
          tva_intra: patch.tvaIntra,
          code_ape: patch.codeApe,
          phone: patch.phone,
          email: patch.email,
          website: patch.website,
          address_line1: patch.addressLine1,
          address_line2: patch.addressLine2,
          postal_code: patch.postalCode,
          city: patch.city,
          iban: patch.iban,
        })
        // `.eq("id", ctx.tenantId)` est obligatoire, pas décoratif : Supabase
        // refuse tout UPDATE sans clause WHERE explicite — « UPDATE requires
        // a WHERE clause » — même quand la RLS limiterait déjà la portée à
        // une seule ligne. La policy reste le vrai garde-fou : cet `.eq`
        // ne fait que satisfaire PostgREST, il ne permet pas de viser un
        // autre tenant, WITH CHECK le refuserait.
        .eq("id", ctx.tenantId)
        .select()
        .single();

      if (error) fail(error);
      return {
        id: data.id,
        name: data.name,
        legalName: data.legal_name ?? null,
        legalForm: data.legal_form ?? null,
        rcs: data.rcs ?? null,
        siret: data.siret ?? null,
        tvaIntra: data.tva_intra ?? null,
        codeApe: data.code_ape ?? null,
        phone: data.phone ?? null,
        email: data.email ?? null,
        website: data.website ?? null,
        addressLine1: data.address_line1 ?? null,
        addressLine2: data.address_line2 ?? null,
        postalCode: data.postal_code ?? null,
        city: data.city ?? null,
        iban: data.iban ?? null,
      };
    },

    async setMyPhone(ctx: ExecutionContext, phone: string): Promise<void> {
      // Par la fonction et non par la table : `public.users` n'accepte plus
      // aucune écriture directe depuis le 11 septembre, parce que la colonne
      // `role` y vivait à côté et se laissait réécrire par n'importe quel
      // membre du tenant. La fonction n'accepte que la ligne de l'appelant.
      const { error } = await clientFor(ctx).rpc("set_my_phone", { p_phone: phone });
      if (error) fail(error);
    },
  };
}
